import { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { BoxTruckOperatorBid, LoadBid } from '@numeo/types'
import { CanonicalLoad } from '@numeo/types-core'
import {
    BoxTruckEmailThread,
    fetchBoxTruckBidsByExternalId,
    fetchBoxTruckEmailThreads,
    fetchBoxTruckOperatorBidsByExternalId,
} from '../services/boxTruckLoadsApi'
import { BoxTruckBidFeed } from '../types/bidFeed'
import { loadKey } from '../utils/loadFields'

/** Where a pinned board's rows come from. One per board — see `driverBidFeedSource` / `myBidFeedSource`. */
export interface BoxTruckBidFeedSource {
    /** Identity of the question being asked, filters included. A change refetches from the first page, so it
     *  must be a VALUE (a joined string), never an object whose identity changes per render. */
    key: string
    pageSize: number
    /** Fetch one page. A source uses whichever cursor its endpoint takes: `offset` is how many rows are
     *  already held, `page` the 1-based page number. `threads` is returned only by a feed whose rows carry
     *  their own email thread; when it is absent the hook fetches the thread overlay instead. */
    fetchPage: (cursor: { offset: number; page: number }) => Promise<{
        loads: CanonicalLoad[]
        total: number
        threads?: Record<string, BoxTruckEmailThread>
        /** The page's loads nobody on this desk has emailed a broker about — the board marks them. Only the
         *  feed whose rows ARE driver bids answers this; for the other it is the whole list by definition. */
        awaitingDesk?: string[]
        /** Whether the SERVER has more rows. Said only where a short page stops meaning the end: the
         *  store-backed bid board filters its page after the query's own LIMIT, so forty rows out of a
         *  hundred-and-fifty-id page is routine and is not the bottom of the list. */
        hasMore?: boolean
        /** Where the NEXT page starts, in the units the server pages by. Said by the same feed as `hasMore`
         *  and for the same reason: there the offset counts ids and the rows are fewer, so continuing from
         *  the number of rows held re-reads ids the server has already handed over and rejected. */
        nextOffset?: number
    }>
    /** Which live events ADD rows to this feed, as against merely changing a row already shown. Getting one
     *  of these wrong is a board that silently stops growing, so each feed states both. */
    rowsAddedBy: {
        /** A driver bidding on a load nobody had bid on makes a new Driver Bids row. */
        driverBid: boolean
        /** A bid email adds a row only to the feed that IS one operator's sends, and only when that operator
         *  sent it: the send event fans out to the sender's desk and to everyone in no group, so reacting to
         *  every one of them refetched this board — back to page one, losing a deep scroll — for work that
         *  can never appear on it. The id to compare the event against, or null when no send adds a row here.
         *
         *  A mismatch (or a missing id) means no refetch, which is the same staleness as the board simply not
         *  being open yet: the next open fetches. It can never show a WRONG list, because what the list
         *  contains is still decided by the server off the token. */
        bidEmailFrom: string | null
    }
    /** Names this feed in the Sentry report when a fetch fails — the two must be distinguishable, or a
     *  board nobody uses and a board everyone uses raise the same alert. */
    label: string
}

/**
 * A pinned bid board's feed: one paginated server list of loads that already carry a bid, plus the overlays
 * the table draws over them.
 *
 * ONE implementation for both pinned boards, parameterised by `source`. They ask different questions —
 * Driver Bids: every load in the project a driver/carrier has bid on, from `load_bids`; My Biddings: every
 * load the CALLER has sent a bid email about, from `interactions` — but the mechanics are identical, and
 * written twice they drifted immediately: the short-page end-of-list rule below existed on one side only,
 * leaving the other able to request pages forever.
 *
 * Isolated from useBoxTruckLoads (the live search feed) so the sources never entangle: the page renders
 * exactly one of them. Fetches only while `enabled`; clears when disabled so a stale list never flashes on
 * re-open.
 */
export const useBoxTruckBidFeed = (enabled: boolean, source: BoxTruckBidFeedSource): BoxTruckBidFeed => {
    const [loads, setLoads] = useState<CanonicalLoad[]>([])
    const [bids, setBids] = useState<Record<string, LoadBid[]>>({})
    const [operatorBids, setOperatorBids] = useState<Record<string, BoxTruckOperatorBid[]>>({})
    const [emailThreads, setEmailThreads] = useState<Record<string, BoxTruckEmailThread>>({})
    // Loads whose driver bid is still waiting for this desk. A Set rather than a list: every rendered row
    // asks the question once per render, and `includes` over a page-sized array is the kind of cost this
    // board has already paid for elsewhere.
    const [awaitingDesk, setAwaitingDesk] = useState<ReadonlySet<string>>(new Set())
    const [loading, setLoading] = useState(false)
    const [answered, setAnswered] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(false)
    // A fetch that failed with rows already held. `error` cannot carry it: that one replaces the board with
    // an error state, which would throw away the rows the operator is reading. Which ACTION failed is kept
    // because the retry differs — and because "couldn't load more" and "couldn't refresh" are different
    // things to be told.
    const [failedFetch, setFailedFetch] = useState<'reload' | 'append' | null>(null)
    const [total, setTotal] = useState(0)
    // Set by a page that came back short — the only end-of-list signal these endpoints give, and `total`
    // alone is not enough: both lists are ordered by a contact time that MOVES, so a bid placed or an email
    // sent mid-paging re-orders rows across the page boundary and the merge drops the repeats. The rows held
    // can therefore stay under the grand total while the pages are already exhausted, and without this the
    // board asks for the next page forever.
    const [reachedEnd, setReachedEnd] = useState(false)
    // Guards against an out-of-order response (a slower earlier fetch resolving after a newer one).
    const reqRef = useRef(0)
    // The last page actually fetched. Counted rather than derived from `loads.length / pageSize`, which
    // dedup makes wrong: one dropped duplicate would re-request the page just read.
    const pageRef = useRef(0)
    // Where the server says the next page starts, where it says so. Null until a page answers with one.
    const nextOffsetRef = useRef<number | null>(null)
    // Mirrors `loads` so the WS handlers and loadMore read the current list without re-subscribing.
    const loadsRef = useRef<CanonicalLoad[]>([])
    loadsRef.current = loads
    const hasMore = !reachedEnd && loads.length < total
    // Read inside the fetch rather than closed over, so `source` may be rebuilt every render (it carries
    // closures) without re-keying anything. `key` is what decides a refetch.
    const sourceRef = useRef(source)
    sourceRef.current = source
    // Likewise `enabled`, and here it is load-bearing rather than tidy: anything holding an OLD `fetchPage`
    // closure would otherwise still see `enabled: true` after the board closed. The debounced reload below is
    // exactly that — a timer scheduled before the close fires after it — and because each fetch bumps
    // `reqRef` itself, the in-flight guard cannot catch it: it would write `loads` and `answered` onto a
    // hidden board. Asking the ref means the only live answer decides.
    const enabledRef = useRef(enabled)
    enabledRef.current = enabled

    // The debounced reload's timer, declared ahead of the open/close effect so that effect can cancel it.
    const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const clearPendingReload = useCallback(() => {
        if (!reloadTimerRef.current) return
        clearTimeout(reloadTimerRef.current)
        reloadTimerRef.current = undefined
    }, [])

    // Fetch one page. `append` = infinite scroll, merged in dedup; else a fresh first-page replace. `total`
    // (the server COUNT before LIMIT/OFFSET) is reported on the first page and kept as the has-more
    // denominator — later pages skip the count, so adopting it there would strand the operator mid-list.
    const fetchPage = useCallback((append: boolean) => {
        if (!enabledRef.current) return
        const { pageSize, fetchPage: fetchFromSource, label } = sourceRef.current
        // THE SERVER'S OFFSET WHERE IT GIVES ONE. `loads.length` is only the right cursor for a feed whose
        // rows and whose offset are the same count; the store-backed bid board filters after its own LIMIT,
        // so continuing from rows held re-read ids it had already been handed and rejected — the board
        // crawled forty rows at a time through hundreds of ids, showing nothing new for most of a scroll.
        const cursor = append ? { offset: nextOffsetRef.current ?? loadsRef.current.length, page: pageRef.current + 1 } : { offset: 0, page: 1 }
        const seq = ++reqRef.current
        if (append) setLoadingMore(true)
        else setLoading(true)
        void fetchFromSource(cursor)
            .then(({ loads: next, total: nextTotal, threads, awaitingDesk: awaiting, hasMore, nextOffset }) => {
                if (seq !== reqRef.current) return
                setError(false)
                setFailedFetch(null)
                pageRef.current = cursor.page
                nextOffsetRef.current = nextOffset ?? null
                // The server's own answer where it gives one; otherwise the short-page rule, which is
                // right for every feed that filters inside its query.
                setReachedEnd(hasMore === undefined ? next.length < pageSize : !hasMore)
                if (!append) setTotal(nextTotal)
                // Merged on append like the overlays, and REPLACED on a reload: a row the desk has emailed
                // since must lose its marker, which a merge would keep forever.
                setAwaitingDesk((prev) => (append ? new Set([...prev, ...(awaiting ?? [])]) : new Set(awaiting ?? [])))
                setLoads((prev) => {
                    if (!append) return next
                    const seen = new Set(prev.map(loadKey))
                    return [...prev, ...next.filter((load) => !seen.has(loadKey(load)))]
                })
                // A feed whose rows carry their own thread needs no overlay call for the Contact column.
                if (threads) {
                    setEmailThreads((prev) => (append ? { ...prev, ...threads } : threads))
                } else {
                    void fetchBoxTruckEmailThreads(next).then((t) => {
                        if (t && seq === reqRef.current) setEmailThreads((prev) => (append ? { ...prev, ...t } : t))
                    })
                }
                // Overlays are best-effort (a failure just hides the indicators), keyed by raw externalId.
                // On append MERGE into the existing maps; on a fresh load, replace. A null result means the
                // fetch FAILED — keep the current overlay rather than wiping it.
                void fetchBoxTruckBidsByExternalId(next).then((b) => {
                    if (b && seq === reqRef.current) setBids((prev) => (append ? { ...prev, ...b } : b))
                })
                void fetchBoxTruckOperatorBidsByExternalId(next).then((o) => {
                    if (o && seq === reqRef.current) setOperatorBids((prev) => (append ? { ...prev, ...o } : o))
                })
            })
            .catch((err) => {
                if (seq !== reqRef.current) return
                // Reported to Sentry, as the map does: a persistently failing endpoint that only reaches
                // one browser console is a board quietly broken for everyone. The last good list stays;
                // the error flag is raised ONLY when there is nothing to show, otherwise the error state
                // would hide rows the operator can still act on.
                Sentry.captureException(err, { tags: { feature: 'box-truck-bid-feed', operation: label } })
                // Nothing to show → the board's error state. Something to show → keep the rows and say on
                // the bottom edge which attempt failed, so the operator is not left reading a list that
                // silently stopped growing, or one that a pressed Refresh never updated.
                if (loadsRef.current.length === 0) setError(true)
                else setFailedFetch(append ? 'append' : 'reload')
            })
            .finally(() => {
                if (seq !== reqRef.current) return
                setLoading(false)
                setLoadingMore(false)
                setAnswered(true)
            })
    }, [])

    const reload = useCallback(() => {
        pageRef.current = 0
        nextOffsetRef.current = null
        setReachedEnd(false)
        fetchPage(false)
    }, [fetchPage])
    const loadMore = useCallback(() => {
        if (loading || loadingMore || reachedEnd || loadsRef.current.length >= total) return
        fetchPage(true)
    }, [loading, loadingMore, reachedEnd, total, fetchPage])

    // Open the board (or change what it asks for) → fetch; leave it → clear, so re-opening never shows the
    // last visit's list while the new fetch is in flight. Keyed on `source.key`, not the object.
    const sourceKey = source.key
    useEffect(() => {
        if (enabled) {
            pageRef.current = 0
            nextOffsetRef.current = null
            setReachedEnd(false)
            fetchPage(false)
        } else {
            // Abandon whatever is in flight. Without this, a fetch that resolves after the board closed still
            // passes the seq check and writes `loads` and `answered` onto a hidden board — so the next open
            // can flash the old list and, worse, treat it as settled (`boardSettled`).
            reqRef.current++
            // And cancel a queued reload. The ref above already stops it fetching, but the pending timer
            // doubles as the "reload already queued" flag: left set across a close and re-open, it would
            // swallow the first real event of the new visit.
            clearPendingReload()
            setLoads([])
            setBids({})
            setOperatorBids({})
            setEmailThreads({})
            setAwaitingDesk(new Set())
            setTotal(0)
            setError(false)
            setFailedFetch(null)
            setLoadingMore(false)
            setAnswered(false)
            setReachedEnd(false)
            pageRef.current = 0
            nextOffsetRef.current = null
        }
    }, [clearPendingReload, enabled, sourceKey, fetchPage])

    // Coalesce a burst of live events into ONE refetch: a bid or a send fans out to every operator, and each
    // off-list event would otherwise fire its own list GET plus the overlay POSTs. A pending timer doubles as
    // the "reload already queued" flag.
    const scheduleReload = useCallback(() => {
        if (reloadTimerRef.current) return
        reloadTimerRef.current = setTimeout(() => {
            reloadTimerRef.current = undefined
            reload()
        }, 500)
    }, [reload])
    useEffect(() => clearPendingReload, [clearPendingReload])

    // Live driver bids: upsert into the overlay for a load already shown. A bid on a load NOT in the list
    // means a new row exists — but only for a feed whose membership a driver's bid decides.
    //
    // The `box-truck:bid-placed` CustomEvent is NOT a second pub/sub channel — it is the bridge OFF the
    // established Redux WS middleware: the socket message lands in
    // store/middleware/handlers/box-truck-loads.ts, which re-emits it as this window event so feature hooks
    // can consume it without each subscribing to the socket. The main board (useBoxTruckLoads) and
    // BoxTruckBidNotifications listen to the same event.
    useEffect(() => {
        if (!enabled) return
        const onBidPlaced = (e: Event) => {
            const bid = (e as CustomEvent<{ bid: LoadBid }>).detail?.bid
            if (!bid?.externalId) return
            if (!loadsRef.current.some((l) => loadKey(l) === bid.externalId)) {
                if (sourceRef.current.rowsAddedBy.driverBid) scheduleReload()
                return
            }
            setBids((prev) => {
                const others = (prev[bid.externalId] ?? []).filter((b) => b.bidderId !== bid.bidderId)
                return { ...prev, [bid.externalId]: [...others, bid].sort((a, b2) => b2.price - a.price) }
            })
        }
        window.addEventListener('box-truck:bid-placed', onBidPlaced)
        return () => window.removeEventListener('box-truck:bid-placed', onBidPlaced)
    }, [enabled, scheduleReload])

    // A sent bid email means the load now HAS a thread — flip the Contact icon live for a row already shown
    // (the next overlay refresh fills in the real thread id). That half stays unfiltered on purpose: on the
    // search board and on Driver Bids the icon means "someone on the team emailed this broker", which is
    // exactly what a teammate's send should say.
    useEffect(() => {
        if (!enabled) return
        const onOperatorBidEmailSent = (e: Event) => {
            const { externalId, operatorId } = (e as CustomEvent<{ externalId?: string; operatorId?: string }>).detail ?? {}
            if (!externalId) return
            if (!loadsRef.current.some((l) => loadKey(l) === externalId)) {
                // Off-list: a new row, but only on the feed that is this operator's own sends. A teammate's
                // send reaches us through the same fan-out and can never appear here.
                const { bidEmailFrom } = sourceRef.current.rowsAddedBy
                if (bidEmailFrom && operatorId === bidEmailFrom) scheduleReload()
                return
            }
            setEmailThreads((prev) => (prev[externalId] ? prev : { ...prev, [externalId]: { emailThreadId: '', unreadCount: 0 } }))
            // And the bid on it is no longer waiting for this desk. Dropped live for the same reason the
            // icon flips: the operator has just sent that email, and a row still marked as unanswered a
            // second afterwards reads as a send that did not land.
            setAwaitingDesk((prev) => {
                if (!prev.has(externalId)) return prev
                const next = new Set(prev)
                next.delete(externalId)
                return next
            })
        }
        window.addEventListener('box-truck:operator-bid-email-sent', onOperatorBidEmailSent)
        return () => window.removeEventListener('box-truck:operator-bid-email-sent', onOperatorBidEmailSent)
    }, [enabled, scheduleReload])

    return { loads, bids, operatorBids, emailThreads, awaitingDesk, loading, answered, error, failedFetch, total, loadMore, hasMore, loadingMore, reload }
}
